export type JsonValue = string | number | boolean | null | JsonObject | JsonArray;
export type JsonObject = { [key: string]: JsonValue };
export type JsonArray = JsonValue[];

export type JsonTreeSelection =
  { type: "line-range"; startLine: number; endLine: number } | { type: "json-path"; path: string } | { type: "none" };

export type LineType = "opening" | "property" | "closing" | "empty";

export interface JsonLine {
  type: LineType;
  depth: number;
  content: string;
  path: (string | number)[];
  isExpandable: boolean;
  isExpanded?: boolean;
  parentId?: string;
  id: string;
  key?: string;
  value?: JsonValue;
  visible: boolean;
  collapsedAncestorId?: string;
}

export function generateJsonTreeLines(data: JsonValue, expandedIds: Set<string>) {
  const allLines = flattenJsonToLines(data, [], 0);
  const lines = markLineVisibility(allLines, expandedIds);
  const visibleLines = lines.filter((line) => line.visible);
  const visibleLinesWithNumbers = visibleLines.map((visibleLine) => {
    const actualLineNumber = lines.findIndex((l) => l.id === visibleLine.id) + 1;
    return { line: visibleLine, lineNumber: actualLineNumber };
  });
  const digits = String(lines.length).length;

  return { lines, visibleLines, visibleLinesWithNumbers, digits };
}

export function isLeafValue(value: JsonValue): boolean {
  return typeof value === "string" || typeof value === "number" || typeof value === "boolean" || value === null;
}

export function formatLeafValue(value: JsonValue): string {
  if (value === null) return "null";
  if (typeof value === "string") return `"${value}"`;
  return String(value);
}

export function getIndent(depth: number): string {
  return "\u00A0".repeat(depth * 2);
}

export function generateLineId(path: (string | number)[], suffix: string): string {
  return `${path.join(".")}.${suffix}`;
}

export function formatJsonAsTreeViewerText(data: JsonValue): string {
  const lines = flattenJsonToLines(data, [], 0);
  return lines.map((line) => `${getIndent(line.depth)}${line.content}`).join("\n");
}

export function collectAllExpandableIds(value: JsonValue, path: (string | number)[] = []): Set<string> {
  const ids = new Set<string>();

  if (isLeafValue(value)) {
    return ids;
  }

  const isArray = Array.isArray(value);
  const isObject = !isArray && typeof value === "object" && value !== null;
  const entries = isArray
    ? (value as JsonArray).map((item, index) => [index, item] as [number, JsonValue])
    : isObject
      ? Object.entries(value as JsonObject)
      : [];

  if (entries.length > 0) {
    const id = generateLineId(path, "container");
    ids.add(id);

    entries.forEach(([key, childValue]) => {
      const childPath = [...path, key];
      const childIds = collectAllExpandableIds(childValue, childPath);
      childIds.forEach((childId) => ids.add(childId));
    });
  }

  return ids;
}

export function flattenJsonToLines(value: JsonValue, path: (string | number)[], depth: number): JsonLine[] {
  const lines: JsonLine[] = [];

  if (isLeafValue(value)) {
    const id = generateLineId(path, "leaf");
    lines.push({
      type: "property",
      depth,
      content: formatLeafValue(value),
      path,
      isExpandable: false,
      id,
      visible: true,
    });
    return lines;
  }

  const isArray = Array.isArray(value);
  const isObject = !isArray && typeof value === "object" && value !== null;
  const entries = isArray
    ? (value as JsonArray).map((item, index) => [index, item] as [number, JsonValue])
    : isObject
      ? Object.entries(value as JsonObject)
      : [];

  if (entries.length === 0) {
    const id = generateLineId(path, "empty");
    lines.push({
      type: "empty",
      depth,
      content: isArray ? "[]" : "{}",
      path,
      isExpandable: false,
      id,
      visible: true,
    });
    return lines;
  }

  const id = generateLineId(path, "container");
  const openBracket = isArray ? "[" : "{";
  const closeBracket = isArray ? "]" : "}";

  lines.push({
    type: "opening",
    depth,
    content: openBracket,
    path,
    isExpandable: true,
    isExpanded: true,
    id,
    visible: true,
  });

  entries.forEach(([key, childValue]) => {
    const childPath = [...path, key];
    const displayKey = typeof key === "number" ? `[${key}]` : `"${key}"`;
    const childId = generateLineId(childPath, "container");

    if (isLeafValue(childValue)) {
      lines.push({
        type: "property",
        depth: depth + 1,
        content: `${displayKey}: ${formatLeafValue(childValue)}`,
        path: childPath,
        isExpandable: false,
        id: generateLineId(childPath, "leaf"),
        key: displayKey,
        value: childValue,
        visible: true,
      });
    } else {
      const childIsArray = Array.isArray(childValue);
      const childIsObject = !childIsArray && typeof childValue === "object" && childValue !== null;
      const childEntries = childIsArray
        ? (childValue as JsonArray).map((item, index) => [index, item] as [number, JsonValue])
        : childIsObject
          ? Object.entries(childValue as JsonObject)
          : [];

      if (childEntries.length === 0) {
        lines.push({
          type: "property",
          depth: depth + 1,
          content: `${displayKey}: ${childIsArray ? "[]" : "{}"}`,
          path: childPath,
          isExpandable: false,
          id: generateLineId(childPath, "empty"),
          visible: true,
        });
      } else {
        lines.push({
          type: "property",
          depth: depth + 1,
          content: `${displayKey}: ${childIsArray ? "[" : "{"}`,
          path: childPath,
          isExpandable: true,
          isExpanded: true,
          id: childId,
          visible: true,
        });
        const childLines = flattenJsonToLines(childValue, childPath, depth + 2);
        lines.push(
          ...childLines.filter(
            (line) => !(line.depth === depth + 2 && (line.type === "opening" || line.type === "closing")),
          ),
        );
        lines.push({
          type: "closing",
          depth: depth + 1,
          content: childIsArray ? "]" : "}",
          path: childPath,
          isExpandable: false,
          id: generateLineId(childPath, "closing"),
          visible: true,
        });
      }
    }
  });

  lines.push({
    type: "closing",
    depth,
    content: closeBracket,
    path,
    isExpandable: false,
    id: generateLineId(path, "closing"),
    visible: true,
  });

  return lines;
}

export function markLineVisibility(lines: JsonLine[], expandedIds: Set<string>): JsonLine[] {
  const collapsedIds = new Set<string>();

  for (const line of lines) {
    if (line.isExpandable && !expandedIds.has(line.id)) {
      collapsedIds.add(line.id);
    }
  }

  return lines.map((line) => {
    if (!line.path || line.path.length === 0) {
      const isExpanded = line.isExpandable ? expandedIds.has(line.id) : undefined;
      return { ...line, isExpanded };
    }

    for (let i = 0; i < line.path.length; i++) {
      const ancestorPath = line.path.slice(0, i);
      const ancestorId = generateLineId(ancestorPath, "container");
      if (collapsedIds.has(ancestorId)) {
        return { ...line, visible: false, collapsedAncestorId: ancestorId, isExpanded: false };
      }
    }

    const containerId = generateLineId(line.path, "container");
    const isExpanded = line.isExpandable ? expandedIds.has(line.id) : undefined;

    if (line.isExpandable && !expandedIds.has(line.id)) {
      const isArray = line.content.endsWith("[");
      const updatedContent = line.content.replace(/[\[\{]$/, isArray ? "[...]" : "{...}");
      return { ...line, visible: true, isExpanded, content: updatedContent };
    }

    if (line.type === "closing" && collapsedIds.has(containerId)) {
      return { ...line, visible: false, isExpanded: false };
    }

    return { ...line, visible: true, collapsedAncestorId: undefined, isExpanded };
  });
}
